import { useEffect, useRef, useState, useCallback } from 'react';
import { apiGet, apiPost } from '@/lib/api';
import {
  Play, Pause, Volume2, VolumeX, Maximize, Subtitles, Gauge,
  RotateCcw, RotateCw, Loader2,
} from 'lucide-react';

/**
 * Lightweight in-app video player for the Cinegraphic Library.
 *
 * Features (Phase 2.33):
 *   - Play / pause with click-anywhere-on-video
 *   - Custom progress bar (scrubbable)
 *   - Volume slider + mute toggle
 *   - Fullscreen toggle (with cross-browser fallbacks)
 *   - Playback speed: 0.75x / 1x / 1.25x / 1.5x / 2x
 *   - Captions / subtitles toggle (uses any provided <track> source)
 *   - ±10s skip buttons (common keyboard shortcuts too)
 *   - Per-student resume position — saves every 5s while playing, resumes on
 *     next open. Backed by /api/video-progress/save + /api/video-progress/get.
 *   - View tracking — a "watch event" is recorded when student reaches 80% of
 *     the video, enabling the rewatch-rate analytic the admin needs.
 *   - Keyboard shortcuts: Space (play/pause), ← → (±10s), ↑ ↓ (volume),
 *     M (mute), F (fullscreen), 0-9 (jump to N×10%)
 *
 * Props:
 *   src             — video file URL (required)
 *   contentId       — content_id for resume / analytics (optional but recommended)
 *   captionsUrl     — optional .vtt captions track URL
 *   poster          — optional thumbnail image URL for the initial frame
 *   onWatched       — callback fired once when student reaches 80% of duration
 *   autoFocus       — whether to capture keyboard shortcuts on mount (default true)
 */
export function CinegraphicPlayer({
  src,
  contentId,
  captionsUrl,
  poster,
  onWatched,
  autoFocus = true,
}) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [captionsOn, setCaptionsOn] = useState(false);
  const [resumeReady, setResumeReady] = useState(!contentId);  // skip resume if no contentId
  const [loadingResume, setLoadingResume] = useState(!!contentId);
  const watchedFiredRef = useRef(false);
  const lastSaveRef = useRef(0);

  // -------- Resume position on mount --------
  useEffect(() => {
    if (!contentId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await apiGet(`/video-progress/${contentId}`);
        if (cancelled || !videoRef.current) return;
        if (r && typeof r.position_sec === 'number' && r.position_sec > 5 && r.position_sec < (r.duration_sec || 1e9) - 5) {
          // Wait for metadata so we can safely seek
          const v = videoRef.current;
          const seek = () => {
            try { v.currentTime = r.position_sec; } catch { /* ignore */ }
          };
          if (v.readyState >= 1) seek();
          else v.addEventListener('loadedmetadata', seek, { once: true });
        }
      } catch {
        // 404 / network — silently start from zero
      } finally {
        if (!cancelled) {
          setLoadingResume(false);
          setResumeReady(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [contentId]);

  // -------- Persist progress every 5s while playing --------
  const saveProgress = useCallback((force = false) => {
    if (!contentId) return;
    const v = videoRef.current;
    if (!v || !v.duration || !isFinite(v.duration)) return;
    const now = Date.now();
    if (!force && now - lastSaveRef.current < 5000) return;
    lastSaveRef.current = now;
    apiPost('/video-progress/save', {
      content_id: contentId,
      position_sec: Math.floor(v.currentTime),
      duration_sec: Math.floor(v.duration),
      completed: v.currentTime / v.duration >= 0.95,
    }).catch(() => { /* non-fatal */ });
  }, [contentId]);

  // -------- Watched-threshold tracking (80%) --------
  useEffect(() => {
    if (!duration || watchedFiredRef.current) return;
    if (currentTime / duration >= 0.8) {
      watchedFiredRef.current = true;
      onWatched && onWatched();
      saveProgress(true);
    }
  }, [currentTime, duration, onWatched, saveProgress]);

  // -------- Player event handlers --------
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play(); else v.pause();
  };
  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    setCurrentTime(v.currentTime);
    if (v.buffered.length) {
      setBuffered(v.buffered.end(v.buffered.length - 1));
    }
    saveProgress();
  };
  const onLoadedMeta = () => {
    if (videoRef.current) setDuration(videoRef.current.duration || 0);
  };
  const onScrub = (e) => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    v.currentTime = pct * v.duration;
    setCurrentTime(v.currentTime);
  };
  const skip = (deltaSec) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min((v.duration || 0), v.currentTime + deltaSec));
  };
  const setSpeedAndClose = (s) => {
    const v = videoRef.current;
    if (v) v.playbackRate = s;
    setSpeed(s);
    setShowSpeedMenu(false);
  };
  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };
  const onVolume = (e) => {
    const v = videoRef.current;
    if (!v) return;
    const val = Number(e.target.value);
    v.volume = val;
    setVolume(val);
    if (val > 0 && v.muted) { v.muted = false; setMuted(false); }
  };
  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen;
      req && req.call(el);
    } else {
      const exit = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen;
      exit && exit.call(document);
    }
  };
  const toggleCaptions = () => {
    const v = videoRef.current;
    if (!v || !v.textTracks || v.textTracks.length === 0) return;
    const next = !captionsOn;
    setCaptionsOn(next);
    for (let i = 0; i < v.textTracks.length; i += 1) {
      v.textTracks[i].mode = next ? 'showing' : 'hidden';
    }
  };

  // -------- Keyboard shortcuts --------
  useEffect(() => {
    if (!autoFocus) return;
    const handler = (e) => {
      // Skip if user is typing in an input/textarea
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      switch (e.key) {
        case ' ': e.preventDefault(); togglePlay(); break;
        case 'ArrowLeft': e.preventDefault(); skip(-10); break;
        case 'ArrowRight': e.preventDefault(); skip(10); break;
        case 'ArrowUp': {
          e.preventDefault();
          const v = videoRef.current;
          if (v) { v.volume = Math.min(1, v.volume + 0.1); setVolume(v.volume); }
          break;
        }
        case 'ArrowDown': {
          e.preventDefault();
          const v = videoRef.current;
          if (v) { v.volume = Math.max(0, v.volume - 0.1); setVolume(v.volume); }
          break;
        }
        case 'm': case 'M': toggleMute(); break;
        case 'f': case 'F': toggleFullscreen(); break;
        case 'c': case 'C': toggleCaptions(); break;
        default:
          if (/^[0-9]$/.test(e.key)) {
            const v = videoRef.current;
            if (v && v.duration) v.currentTime = (Number(e.key) / 10) * v.duration;
          }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus]);

  // -------- Save on unmount (best-effort) --------
  useEffect(() => {
    return () => saveProgress(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fmt = (s) => {
    if (!s || !isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const ss = Math.floor(s % 60).toString().padStart(2, '0');
    return `${m}:${ss}`;
  };

  const pct = duration ? (currentTime / duration) * 100 : 0;
  const bufPct = duration ? (buffered / duration) * 100 : 0;

  return (
    <div
      ref={containerRef}
      className="relative bg-black rounded-3xl overflow-hidden group/player select-none"
      data-testid="cinegraphic-player"
    >
      {loadingResume && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/60 backdrop-blur-sm pointer-events-none">
          <Loader2 className="w-8 h-8 text-white animate-spin" />
        </div>
      )}
      {/* Video element — click to play/pause. controlsList + disablePictureInPicture
          prevent the browser's native context-menu "Download video" / "Save as"
          / "Picture-in-Picture" affordances that would otherwise let users
          extract a local copy. disableRemotePlayback also blocks AirPlay-style
          casting that some recording apps tap into. */}
      <video
        ref={videoRef}
        src={resumeReady ? src : undefined}
        poster={poster}
        preload="metadata"
        crossOrigin="anonymous"
        controlsList="nodownload noremoteplayback"
        disablePictureInPicture
        disableRemotePlayback
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); saveProgress(true); }}
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={onLoadedMeta}
        onClick={togglePlay}
        onContextMenu={(e) => e.preventDefault()}
        className="w-full h-auto block cursor-pointer"
        data-testid="cinegraphic-player-video"
      >
        {captionsUrl && (
          <track kind="subtitles" src={captionsUrl} srcLang="en" label="English" default={false} />
        )}
        Your browser does not support video.
      </video>

      {/* Center play icon overlay (when paused) */}
      {!playing && !loadingResume && (
        <button
          onClick={togglePlay}
          aria-label="Play video"
          data-testid="cinegraphic-player-center-play"
          className="absolute inset-0 z-10 flex items-center justify-center bg-gradient-to-t from-black/40 via-transparent to-transparent"
        >
          <div className="w-20 h-20 rounded-full bg-white/95 flex items-center justify-center shadow-2xl hover:scale-110 transition-transform">
            <Play className="w-8 h-8 text-purple-600 ml-1" fill="currentColor" />
          </div>
        </button>
      )}

      {/* Bottom control bar */}
      <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black/95 via-black/70 to-transparent pt-12 pb-3 px-4 opacity-0 group-hover/player:opacity-100 focus-within:opacity-100 transition-opacity">
        {/* Progress bar */}
        <div
          className="relative h-1.5 mb-3 cursor-pointer rounded-full bg-white/20 group/bar"
          onClick={onScrub}
          data-testid="cinegraphic-player-progress"
        >
          {/* Buffered */}
          <div className="absolute inset-y-0 left-0 bg-white/35 rounded-full" style={{ width: `${bufPct}%` }} />
          {/* Played */}
          <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full" style={{ width: `${pct}%` }} />
          {/* Scrub thumb */}
          <div
            className="absolute top-1/2 w-3 h-3 -mt-1.5 -ml-1.5 rounded-full bg-white shadow-lg opacity-0 group-hover/bar:opacity-100 transition-opacity"
            style={{ left: `${pct}%` }}
          />
        </div>

        <div className="flex items-center gap-2 text-white">
          <button onClick={togglePlay} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="cinegraphic-player-playpause" title={playing ? 'Pause (space)' : 'Play (space)'}>
            {playing ? <Pause className="w-5 h-5" fill="currentColor" /> : <Play className="w-5 h-5" fill="currentColor" />}
          </button>
          <button onClick={() => skip(-10)} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="cinegraphic-player-back10" title="Back 10s (←)">
            <RotateCcw className="w-4 h-4" />
          </button>
          <button onClick={() => skip(10)} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="cinegraphic-player-fwd10" title="Forward 10s (→)">
            <RotateCw className="w-4 h-4" />
          </button>

          {/* Volume */}
          <div className="flex items-center gap-1.5 ml-1 group/vol">
            <button onClick={toggleMute} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="cinegraphic-player-mute" title={muted ? 'Unmute (M)' : 'Mute (M)'}>
              {muted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <input type="range" min="0" max="1" step="0.05" value={muted ? 0 : volume}
              onChange={onVolume}
              data-testid="cinegraphic-player-volume"
              className="w-0 group-hover/vol:w-16 transition-all duration-200 accent-purple-400 h-1 cursor-pointer" />
          </div>

          {/* Time */}
          <span className="text-xs font-mono ml-2 tabular-nums" data-testid="cinegraphic-player-time">
            {fmt(currentTime)} / {fmt(duration)}
          </span>

          <div className="flex-1" />

          {/* Speed menu */}
          <div className="relative">
            <button onClick={() => setShowSpeedMenu(v => !v)}
              className={`px-2.5 h-8 rounded-lg text-xs font-heading flex items-center gap-1 transition-colors ${showSpeedMenu ? 'bg-white/20' : 'hover:bg-white/10'}`}
              data-testid="cinegraphic-player-speed-toggle" title="Playback speed">
              <Gauge className="w-3.5 h-3.5" /> {speed}x
            </button>
            {showSpeedMenu && (
              <div className="absolute right-0 bottom-full mb-1 bg-slate-900/95 backdrop-blur rounded-lg border border-white/10 py-1 min-w-[80px] shadow-2xl"
                data-testid="cinegraphic-player-speed-menu">
                {[0.75, 1, 1.25, 1.5, 2].map(s => (
                  <button key={s} onClick={() => setSpeedAndClose(s)}
                    className={`w-full px-3 py-1.5 text-xs font-heading text-left hover:bg-white/10 transition-colors ${s === speed ? 'text-purple-300' : 'text-white'}`}
                    data-testid={`cinegraphic-player-speed-${s}`}>
                    {s}x{s === 1 ? ' (normal)' : ''}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Captions toggle (only if track present) */}
          {captionsUrl && (
            <button onClick={toggleCaptions}
              className={`p-1.5 rounded-lg transition-colors ${captionsOn ? 'bg-white/20' : 'hover:bg-white/10'}`}
              data-testid="cinegraphic-player-captions" title="Captions (C)">
              <Subtitles className="w-4 h-4" />
            </button>
          )}

          {/* Fullscreen */}
          <button onClick={toggleFullscreen} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="cinegraphic-player-fullscreen" title="Fullscreen (F)">
            <Maximize className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
